import { useEffect, useState } from "react";
import api, { fmtMoney, fmtDate, apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Coins, Eye, Send, Mail, MessageSquare, Bell } from "lucide-react";
import { toast } from "sonner";

function Kpi({ label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-700", emerald: "text-emerald-600", rose: "text-rose-600" };
  return (
    <div className="rounded-xl border bg-white p-4">
      <p className="text-[10px] uppercase font-mono text-slate-400">{label}</p>
      <p className={`font-mono font-bold text-lg ${tones[tone]}`}>{fmtMoney(value)}</p>
    </div>
  );
}

export default function Creances() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/receivables").then((r) => setData(r.data)).catch(() => setData({ clients: [] })); }, []);
  const openDetail = (clientId) => api.get(`/receivables/${clientId}`).then((r) => setDetail(r.data));
  const [relancing, setRelancing] = useState(false);
  const relancer = async () => {
    setRelancing(true);
    try { const { data } = await api.post(`/receivables/${detail.client.id}/relance`); toast.success(`Relance envoyée (palier ${data.palier} — ${data.titre})${data.email ? " par e-mail" : ""}`); const r = await api.get(`/receivables/${detail.client.id}`); setDetail(r.data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setRelancing(false); }
  };

  if (!data) return <Loader />;
  return (
    <div>
      <PageHeader title="Créances clients" subtitle="Situation globale et détaillée des créances" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <Kpi label="Total facturé" value={data.total_facture} />
        <Kpi label="Total encaissé" value={data.total_paye} tone="emerald" />
        <Kpi label="Solde restant dû" value={data.solde} tone="rose" />
      </div>

      {(data.clients || []).length === 0 ? (
        <EmptyState icon={Coins} title="Aucune créance" subtitle="Les créances apparaîtront dès qu'une facture est émise." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Client</TableHead><TableHead>Factures</TableHead><TableHead>Facturé</TableHead><TableHead>Encaissé</TableHead><TableHead>Solde dû</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {data.clients.map((c) => (
                <TableRow key={c.client_id || c.nom} className="hover:bg-slate-50" data-testid={`creance-row-${c.client_id || c.nom}`}>
                  <TableCell className="font-semibold">{c.nom}</TableCell>
                  <TableCell className="text-sm text-slate-500">{c.nb}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtMoney(c.total_facture)}</TableCell>
                  <TableCell className="font-mono text-xs text-emerald-600">{fmtMoney(c.total_paye)}</TableCell>
                  <TableCell className={`font-mono text-xs font-bold ${c.solde > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtMoney(c.solde)}</TableCell>
                  <TableCell>{c.client_id && <Button variant="ghost" size="icon" onClick={() => openDetail(c.client_id)} data-testid={`creance-view-${c.client_id}`}><Eye className="h-4 w-4 text-brand-600" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {detail && (<>
            <DialogHeader><DialogTitle className="font-head">{detail.client?.nom || "Client"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Kpi label="Facturé" value={detail.total_facture} />
              <Kpi label="Encaissé" value={detail.total_paye} tone="emerald" />
              <Kpi label="Solde" value={detail.solde} tone="rose" />
            </div>
            {detail.solde > 0 && detail.client?.id && (
              <div className="mb-3 flex justify-end">
                <Button onClick={relancer} disabled={relancing} data-testid="relancer-btn" className="bg-brand-600 hover:bg-brand-700 text-white"><Send className="h-4 w-4 mr-1.5" />{relancing ? "Envoi…" : "Relancer maintenant"}</Button>
              </div>
            )}
            <p className="text-xs font-mono uppercase text-slate-400 mb-1">Factures</p>
            <div className="rounded-lg border overflow-hidden mb-3">
              <table className="w-full text-xs"><thead className="bg-slate-50 text-slate-500"><tr><th className="text-left p-1.5">N°</th><th className="p-1.5">Date</th><th className="p-1.5">Échéance</th><th className="p-1.5">TTC</th><th className="p-1.5">Payé</th><th className="p-1.5">Solde</th></tr></thead>
                <tbody>{detail.invoices.map((i) => (
                  <tr key={i.numero} className="border-t"><td className="p-1.5 font-mono">{i.numero}</td><td className="p-1.5 text-center">{fmtDate(i.date)}</td><td className="p-1.5 text-center">{i.echeance ? fmtDate(i.echeance) : "—"}</td><td className="p-1.5 text-right font-mono">{fmtMoney(i.total_ttc)}</td><td className="p-1.5 text-right font-mono text-emerald-600">{fmtMoney(i.montant_paye)}</td><td className="p-1.5 text-right font-mono text-rose-600">{fmtMoney(i.solde)}</td></tr>
                ))}</tbody>
              </table>
            </div>
            <p className="text-xs font-mono uppercase text-slate-400 mb-1">Historique des encaissements</p>
            <div className="rounded-lg border overflow-hidden mb-3">
              <table className="w-full text-xs"><tbody>
                {detail.history.length === 0 ? <tr><td className="p-2 text-slate-400 text-center">Aucun encaissement</td></tr> : detail.history.map((h, i) => (
                  <tr key={i} className="border-t"><td className="p-1.5">{fmtDate(h.date)}</td><td className="p-1.5 font-mono">{h.invoice}</td><td className="p-1.5">{h.mode}</td><td className="p-1.5 text-right font-mono text-emerald-600">{fmtMoney(h.montant)}</td></tr>
                ))}
              </tbody></table>
            </div>
            <p className="text-xs font-mono uppercase text-slate-400 mb-1">Journal des relances</p>
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left p-1.5">Date</th><th className="text-left p-1.5">Palier</th><th className="p-1.5">Factures</th><th className="p-1.5">Canal</th><th className="p-1.5 text-right">Montant dû</th></tr></thead>
                <tbody>
                  {(detail.relances || []).length === 0 ? <tr><td colSpan={5} className="p-2 text-slate-400 text-center">Aucune relance envoyée</td></tr> : detail.relances.map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`relance-row-${i}`}>
                      <td className="p-1.5">{fmtDate(r.date)}</td>
                      <td className="p-1.5"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.palier >= 3 ? "bg-rose-100 text-rose-700" : r.palier === 2 ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}`}>P{r.palier} · {r.titre}</span></td>
                      <td className="p-1.5 text-center">{r.nb_factures}</td>
                      <td className="p-1.5 text-center">
                        <span className="inline-flex items-center gap-1 flex-wrap justify-center">
                          {(r.canal || "").includes("e-mail") && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-brand-50 text-brand-700 border border-brand-200"><Mail className="h-2.5 w-2.5" />E-mail</span>}
                          {(r.canal || "").includes("SMS") && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200"><MessageSquare className="h-2.5 w-2.5" />SMS</span>}
                          {(r.canal || "").includes("notification") && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600 border"><Bell className="h-2.5 w-2.5" />Notif</span>}
                          {r.manuel && <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700 border border-amber-200">Manuel</span>}
                        </span>
                      </td>
                      <td className="p-1.5 text-right font-mono text-rose-600">{fmtMoney(r.total_du)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
